/**
 * Badge styling for a `membership_payments` row's status.
 *
 * Extracted from MembershipCard so the mapping is testable and so adding
 * a status is a one-line change in one place (STARSEED-CAPMARKETS-120).
 *
 * The bug this prevents: the mapping used to be an inline ternary chain
 * whose final `else` was the rose/FAILURE style. Any status the chain did
 * not name — `waived` being the first new one in a year — rendered a
 * settled, good outcome as though the payment had failed. A fall-through
 * default that means "error" is the wrong default; unknown statuses are
 * now neutral, and only statuses we explicitly classify as failures get
 * the failure style.
 */

const EMERALD = "bg-emerald-50 text-emerald-700 border-emerald-200 border";
const AMBER = "bg-amber-50 text-amber-700 border-amber-200 border";
const ROSE = "bg-rose-50 text-rose-700 border-rose-200 border";
const SLATE = "bg-slate-50 text-slate-700 border-slate-200 border";

/** Settled — the member owes nothing for this row. */
export const SETTLED_STATUSES = ["paid", "waived"];

/** Outstanding — money is expected, nothing has gone wrong. */
export const PENDING_STATUSES = ["pending", "deferred"];

/** Genuinely bad outcomes. Only these get the failure style. */
export const FAILED_STATUSES = ["failed", "refunded", "chargeback", "canceled", "cancelled"];

/**
 * @param {string|undefined|null} status
 * @returns {string} Tailwind class string for the status badge.
 */
export function membershipStatusBadgeClass(status) {
  const s = String(status || "").trim().toLowerCase();
  if (SETTLED_STATUSES.includes(s)) return EMERALD;
  if (PENDING_STATUSES.includes(s)) return AMBER;
  if (FAILED_STATUSES.includes(s)) return ROSE;
  // Unknown status: neutral, NOT failure. We would rather show a member an
  // uncoloured badge we haven't classified than tell them a settled fee
  // failed.
  return SLATE;
}

export default membershipStatusBadgeClass;
